import React, { useMemo, useState } from 'react';
import { getProjectTotalExpenses } from '../../services/expenseFirestore';
import DataTable from '../ui/DataTable';
import Modal from '../ui/Modal';

function profitColor(value) {
  if (value > 0) return '#15803d';
  if (value < 0) return '#b91c1c';
  return '#0f2554';
}

export default function ProfitLoss({ projects, expenses, invoices, loading }) {
  const [lossDetail, setLossDetail] = useState(null);

  const rows = useMemo(() => {
    return projects
      .filter((p) => p.status === 'Completed')
      .map((p) => {
        const totalExpense = getProjectTotalExpenses(expenses, p.id);
        const value = Number(p.projectValue) || 0;
        const profit = value - totalExpense;
        const loss = totalExpense - value;
        const inv = invoices.find((i) => i.projectDocId === p.id);
        const duration = p.startDate && p.completedDate ? `${p.startDate} → ${p.completedDate}` : '—';
        return {
          ...p,
          invoiceNo: inv?.invoiceId || '—',
          duration,
          value,
          totalExpense,
          profit,
          loss,
          displayAmount: profit >= 0 ? profit : -loss,
        };
      });
  }, [projects, expenses, invoices]);

  const columns = [
    { key: 'projectId', label: 'Project ID' },
    { key: 'projectName', label: 'Project Name' },
    { key: 'invoiceNo', label: 'Invoice No' },
    { key: 'duration', label: 'Duration' },
    { key: 'value', label: 'Value', render: (r) => `₹ ${r.value.toLocaleString('en-IN')}` },
    {
      key: 'profit',
      label: 'Profit / Loss',
      render: (r) => {
        const isLoss = r.profit < 0;
        const amount = isLoss ? r.loss : r.profit;
        return (
          <button
            type="button"
            className="exp-pl-amount"
            style={{ color: profitColor(r.profit), fontWeight: 700, background: 'none', border: 'none', cursor: isLoss ? 'pointer' : 'default', textDecoration: isLoss ? 'underline' : 'none' }}
            onClick={() => isLoss && setLossDetail(r)}
          >
            {isLoss ? `-₹ ${amount.toLocaleString('en-IN')}` : `₹ ${amount.toLocaleString('en-IN')}`}
            {isLoss && <span style={{ fontSize: '0.75rem', marginLeft: 4 }}>(Loss)</span>}
          </button>
        );
      },
    },
  ];

  const detailExpenses = lossDetail
    ? expenses.filter((e) => e.projectDocId === lossDetail.id)
    : [];

  return (
    <div className="expense-panel">
      <div className="expense-card-header">
        <h3 className="expense-panel-title">Profit & Loss</h3>
      </div>
      {loading ? <p className="expense-loading">Loading…</p> : (
        <DataTable columns={columns} data={rows} searchKeys={['projectId', 'projectName']} searchPlaceholder="Search completed projects…" />
      )}

      <Modal open={!!lossDetail} onClose={() => setLossDetail(null)} title="Loss Detail" size="lg">
        {lossDetail && (
          <>
            <div className="exp-status-detail"><strong>Project:</strong> {lossDetail.projectName} ({lossDetail.projectId})</div>
            <div className="exp-status-detail"><strong>Client:</strong> {lossDetail.client || '—'}</div>
            <div className="exp-status-detail"><strong>Duration:</strong> {lossDetail.duration}</div>
            <h4 style={{ marginTop: '1.5rem' }}>Expense List</h4>
            <div className="ui-data-table-wrap">
              <table>
                <thead><tr><th>Expense</th><th>Amount</th><th>Date</th><th>Employee</th></tr></thead>
                <tbody>
                  {detailExpenses.length === 0 ? (
                    <tr><td colSpan={4} className="ui-data-table-empty">No expenses</td></tr>
                  ) : detailExpenses.map((e) => (
                    <tr key={e.id}>
                      <td>{e.expense}</td>
                      <td>₹ {Number(e.amount).toLocaleString('en-IN')}</td>
                      <td>{e.date}</td>
                      <td>{e.employeeName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="exp-pl-summary">
              <div><strong>Total Expense:</strong> ₹ {lossDetail.totalExpense.toLocaleString('en-IN')}</div>
              <div><strong>Project Value:</strong> ₹ {lossDetail.value.toLocaleString('en-IN')}</div>
              <div style={{ color: '#b91c1c' }}><strong>Difference (Loss):</strong> ₹ {lossDetail.loss.toLocaleString('en-IN')}</div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
